/** 운영자 — 전체 회원(학부모·원장) 목록 + 검색 (원장 연결용 uid 찾기) */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { IoSearchOutline, IoCopyOutline, IoTrashOutline } from 'react-icons/io5';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { PageHead, PageTitle, Card, Table, Empty, IconBtn } from '../Console/consoleUi';

const isDemoUser = (u) => u.uid.startsWith('demo-') || !!u.demoSessionId || u.provider === 'demo';

const acctLabel = (u) => {
  if (u.accountType === 'academy') return '학원/원장';
  if (u.accountType === 'parent') return '학부모';
  if (u.role === 'parent') return '학부모';
  return u.role || '-';
};

const OperatorMembersPage = () => {
  const [users, setUsers] = useState([]);
  const [demoCount, setDemoCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const snap = await getDocs(collection(db, 'users'));
    const all = snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
    const real = all.filter((u) => !isDemoUser(u));
    real.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    setUsers(real);
    setDemoCount(all.length - real.length);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return users;
    return users.filter((u) => `${u.name || ''} ${u.displayName || ''} ${u.kakaoNickname || ''} ${u.uid} ${u.email || ''}`.toLowerCase().includes(t));
  }, [users, q]);

  const copy = (uid) => { try { navigator.clipboard.writeText(uid); } catch {} };
  const onDelete = async (u) => {
    if (u.uid === 'superadmin') { alert('운영자 계정은 삭제할 수 없어요.'); return; }
    if (!window.confirm(`'${u.name || u.displayName || u.uid}' 회원을 삭제할까요?\n되돌릴 수 없어요.`)) return;
    await deleteDoc(doc(db, 'users', u.uid));
    await load();
  };

  return (
    <>
      <PageHead>
        <PageTitle>
          <h2>회원 관리</h2>
          <p>실제 회원 {users.length}명{demoCount ? ` · 데모 ${demoCount}개 숨김` : ''} · 닉네임으로 검색해 원장 연결용 uid를 확인하세요.</p>
        </PageTitle>
        <SearchBox>
          <IoSearchOutline size={18} color="#9CA3AF" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="이름 · 닉네임 · uid 검색" />
        </SearchBox>
      </PageHead>

      <Card $pad="0">
        {loading ? <Empty>불러오는 중…</Empty> : filtered.length === 0 ? (
          <Empty>해당하는 회원이 없어요.</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>표시명</th><th>카카오 닉네임</th><th>유형</th><th>가입</th><th>uid</th>
                <th style={{ width: 90, textAlign: 'right' }}>관리</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.uid}>
                  <td style={{ fontWeight: 700, color: '#111827' }}>{u.name || u.displayName || '(무명)'}</td>
                  <td>{u.kakaoNickname || <span style={{ color: '#C4C9D2' }}>-</span>}</td>
                  <td>{acctLabel(u)}</td>
                  <td>{u.provider || '-'}</td>
                  <td><Uid>{u.uid}</Uid></td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <IconBtn onClick={() => copy(u.uid)} title="uid 복사"><IoCopyOutline size={16} /></IconBtn>
                    <IconBtn $danger onClick={() => onDelete(u)} title="삭제"><IoTrashOutline size={16} /></IconBtn>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
};

export default OperatorMembersPage;

const SearchBox = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 9px 14px;
  background: #fff; border: 1px solid #E2E5EA; border-radius: 9px; width: 280px;
  input { border: none; outline: none; font-size: 14px; width: 100%; color: #374151; }
`;
const Uid = styled.span` font-size: 12.5px; color: #6B7280; font-family: ui-monospace, monospace; `;
